<template>
  <section v-if="newEvent" class="deployment-sensors">
    <div class="event-form--section-title">
      <div class="section-title__tooltip">Sensors</div>
      List of all Sensors assigned to your event
    </div>
    <div class="event-form--content">
      <div v-if="newEvent.zones.length === 0" class="event-form--zones-empty">
        <IconZones />
        <strong>No Sensors to Show yet</strong>
        <p>Create a zone to add sensor(s) and zone types</p>
      </div>
      <div v-else>
        <div class="table-filters">
          <InputText name="search" v-model="search" label="Search" />
          <div>
            <FloatLabel variant="on">
              <Select
                id="sort_by"
                v-model="sortBy"
                :options="sortOptions"
                optionValue="id"
                optionLabel="label"
              />
              <label for="sort_by">Sort By</label>
            </FloatLabel>

            <FloatLabel variant="on">
              <Select
                id="sort_desc"
                v-model="sortDir"
                :options="sortDirOptions"
                optionValue="id"
                optionLabel="label"
              />
              <label for="sort_desc">Sort Dir</label>
            </FloatLabel>
          </div>
        </div>

        <div v-if="search && !sortedZones?.length">No sensors match your search terms.</div>

        <Accordion v-model:value="activeZones" multiple>
          <template #collapseicon><IconCaretUp /></template>
          <template #expandicon><IconCaretDown /></template>

          <template v-for="(zone, index) in sortedZones">
            <AccordionPanel
              v-for="(sensor, senIndex) in zone.sensors"
              :key="`${sensor.id}-${senIndex}`"
              :value="index"
            >
              <AccordionHeader>
                <div class="header">
                  <label>{{ sensor.name }}</label>
                  <div>
                    <Chip>{{ sensor.mac_address }}</Chip>
                  </div>
                  <span>(Zone: {{ zone.name }})</span>
                </div>
              </AccordionHeader>
              <AccordionContent>
                <DeploymentSensor
                  v-model:sensor="zone.sensors[senIndex]"
                  :sensorDistanceOptions="sensorDistanceOptions"
                />
              </AccordionContent>
            </AccordionPanel>
          </template>
        </Accordion>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import type { SensorDistanceSettingType } from '@/types/Sensor';
import type { EventTypeNew } from '@/types/Event';

import InputText from '@/components/inputs/InputText.vue';
import {
  Accordion,
  AccordionPanel,
  AccordionHeader,
  AccordionContent,
  FloatLabel,
  Select,
} from 'primevue';

import Chip from '../global/Chip.vue';
import DeploymentSensor from './DeploymentSensor.vue';

import IconCaretUp from '@/icons/arrows/IconCaretUp.svg?component';
import IconCaretDown from '@/icons/arrows/IconCaretDown.svg?component';
import IconZones from '@/icons/events/IconZones.svg?component';

const newEvent = defineModel<EventTypeNew>('newEvent');

defineProps<{
  sensorDistanceOptions: SensorDistanceSettingType[];
}>();

const sortOptions = [
  { id: 'name', label: 'Sensor Name' },
  { id: 'zone', label: 'Zone Name' },
  { id: 'address', label: 'Mac Address' },
];

const sortDirOptions = [
  { id: 'asc', label: 'Ascending' },
  { id: 'desc', label: 'Descending' },
];

const activeZones = ref([0]);
const sortBy = ref<string>(sortOptions[0].id);
const sortDir = ref<string>(sortDirOptions[0].id);
const search = ref<string>('');

const sortedZones = computed(() => {
  const allZones = ref(newEvent.value?.zones);

  if (search.value.trim() != '') {
    const term = search.value.toLowerCase();

    allZones.value = allZones.value?.filter((zone) => {
      if (zone.name.toLowerCase().includes(term)) {
        return true;
      }
      if (zone.sensors[0].name?.toLowerCase().includes(term)) {
        return true;
      }
      if (zone.sensors[0].mac_address?.toLowerCase().includes(term)) {
        return true;
      }

      return false;
    });
  }

  if (sortBy.value === 'default') {
    return allZones.value;
  }

  if (sortBy.value === 'zone') {
    allZones.value = allZones.value?.sort((a, b) => {
      const zoneA = a.name.toLowerCase() || '';
      const zoneB = b.name.toLowerCase() || '';

      if (sortDir.value === 'asc') {
        return zoneA > zoneB ? 1 : zoneA < zoneB ? -1 : 0;
      } else {
        return zoneA < zoneB ? 1 : zoneA > zoneB ? -1 : 0;
      }
    });

    return allZones.value;
  }

  if (sortBy.value === 'address') {
    allZones.value = allZones.value?.sort((a, b) => {
      const zoneA = a.sensors[0].mac_address?.toLowerCase() || '';
      const zoneB = b.sensors[0].mac_address?.toLowerCase() || '';

      if (sortDir.value === 'asc') {
        return zoneA > zoneB ? 1 : zoneA < zoneB ? -1 : 0;
      } else {
        return zoneA < zoneB ? 1 : zoneA > zoneB ? -1 : 0;
      }
    });

    return allZones.value;
  }

  if (sortBy.value === 'name') {
    allZones.value = allZones.value?.sort((a, b) => {
      const zoneA = a.sensors[0].name?.toLowerCase() || '';
      const zoneB = b.sensors[0].name?.toLowerCase() || '';

      if (sortDir.value === 'asc') {
        return zoneA > zoneB ? 1 : zoneA < zoneB ? -1 : 0;
      } else {
        return zoneA < zoneB ? 1 : zoneA > zoneB ? -1 : 0;
      }
    });

    return allZones.value;
  }

  return allZones.value;
});
</script>

<style lang="scss">
.deployment-sensors {
  .p-accordionheader {
    background-color: transparent;
  }

  .p-accordionheader .header {
    @include mixins.flex-center($horiz: space-between, $gap: variables.$vantage-base-sizing-sm);
    margin-right: variables.$vantage-base-sizing-lg;
    width: 100%;

    label {
      @include typography.font-body($size: md, $weight: medium);
      color: variables.$vantage-base-primitives-color-tertiary-500;
      padding-left: variables.$vantage-base-sizing-xs;
    }

    > label,
    > div,
    > span {
      flex-basis: 33%;
      padding-left: variables.$vantage-base-sizing-xs;
    }
  }

  .p-accordionpanel {
    margin-bottom: variables.$vantage-base-sizing-sm;
  }

  .p-accordioncontent-content {
    border-bottom-right-radius: variables.$vantage-base-sizing-xxs;
    border-bottom-left-radius: variables.$vantage-base-sizing-xxs;
    border: 1px solid variables.$vantage-base-primitives-color-border-100;
    border-top: 0;
    padding: 0;
  }

  .sensor-settings-row {
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-sm);
    @include sizing.padding('xl', 'xxl', 'sm', 'xxs');
  }

  .p-accordionpanel:first-child > .p-accordionheader {
    background: transparent;
    border-width: var(--p-accordion-header-first-border-width);
    border-top-left-radius: var(--p-accordion-header-first-top-border-radius);
    border-top-right-radius: var(--p-accordion-header-first-top-border-radius);
  }

  .p-accordionpanel:not(.p-disabled).p-accordionpanel-active > .p-accordionheader {
    background: var(--p-accordion-header-background);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.table-filters > div {
  @include mixins.flex-center($horiz: flex-end, $gap: variables.$vantage-base-sizing-xs);
}

.p-dialog .modal-body .deployment-sensors .table-filters {
  width: 100%;

  > div {
    @include mixins.flex-center($horiz: flex-end, $gap: variables.$vantage-base-sizing-xs);
    width: calc(50% - variables.$vantage-base-sizing-xs);

    .p-floatlabel {
      flex-basis: calc(50% - 12px);
    }
  }
}

.p-dialog .modal-body .deployment-sensors .table-filters {
  .p-floatlabel {
    flex-basis: 100px;
    min-width: 100px;
    width: auto;
  }

  > .p-floatlabel {
    flex-basis: 250px;
  }
}
</style>
